import cn from 'classnames';
import Image from '@components/ui/image';
import usePrice, { useGetPrice } from '@framework/product/use-price';
import { getNameForLanguage, getProductStock, Product } from '@framework/types';
import { useModalAction } from '@components/common/modal/modal.context';
import useWindowSize from '@utils/use-window-size';
import { Eye } from '@components/icons/eye-icon';
import { useCart } from '@contexts/cart/cart.context';
import React, { Suspense } from 'react';
import { useTranslation } from '@i18n/client';
import { getProductImageUrl } from '@framework/product/get-product-image';
import Link from '@components/ui/link';
import { useAuthContext } from '@contexts/use-auth-context';
import { ProductUnitLabelTranslations } from '@components/product/helpers/TranslationsHelper';
import { KosherIcon } from '@components/product/helpers/KosherIcon';

const AddToCart = React.lazy(() => import('@components/product/add-to-cart'));

interface ProductProps {
  lang: string;
  product: Product;
  className?: string;
}

function RenderPopupOrAddToCart({
  product,
  lang,
}: {
  product: Product;
  lang: string;
}) {
  const { isAuthenticated } = useAuthContext();
  const { t } = useTranslation(lang, 'common');

  const batch = product.batches.length == 1 ? product.batches[0] : null;

  const { width } = useWindowSize();
  const { openModal } = useModalAction();
  const { isInCart, isInStock } = useCart();
  const iconSize = width! > 1024 ? '19' : '17';
  const outOfStock =
    isInCart(product.id, batch?.batch_id) &&
    !isInStock(product.id, batch?.batch_id);

  if (!isAuthenticated) {
    return <></>;
  }

  function handlePopupView() {
    openModal('PRODUCT_VIEW', product);
  }

  if (getProductStock(product) < 1) {
    return (
      <span
        className={`text-[11px] md:text-xs font-bold text-brand-light uppercase inline-block rounded-full px-2.5 pt-1 pb-[3px] mx-0.5 sm:mx-1 ${product.coming_soon ? 'bg-brand-dark' : 'bg-brand-danger'}`}
      >
        {product.coming_soon ? t('text-coming-soon') : t('text-out-stock')}
      </span>
    );
  }
  if (product.batches?.length > 1) {
    return (
      <button
        className="inline-flex items-center justify-center w-8 h-8 text-4xl rounded-full bg-brand lg:w-10 lg:h-10 text-brand-light focus:outline-none focus-visible:outline-none"
        aria-label="Count Button"
        onClick={handlePopupView}
      >
        <Eye width={iconSize} height={iconSize} opacity="1" />
      </button>
    );
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AddToCart
        product={product}
        batch={batch}
        variant="mercury"
        lang={lang}
      />
    </Suspense>
  );
}
const ProductCardAlpine: React.FC<ProductProps> = ({
  product,
  className,
  lang,
}) => {
  const productImage = getProductImageUrl(product);
  const { unit, product_type, items_per_unit } = product ?? {};
  const { t } = useTranslation(lang, 'common');
  const singleBatch = product.batches.length == 1 ? product.batches[0] : null;
  const { price, basePrice, discount } = useGetPrice(
    product,
    singleBatch,
    'EUR',
  );
  const { price: minPrice } = usePrice({
    amount: product?.min_price ?? 0,
    currencyCode: 'EUR',
  });
  const { price: maxPrice } = usePrice({
    amount: product?.max_price ?? 0,
    currencyCode: 'EUR',
  });

  return (
    <article
      className={cn(
        'flex flex-col group overflow-hidden rounded-md transition-all duration-300 shadow-card hover:shadow-cardHover relative h-full',
        className,
      )}
      title={getNameForLanguage(product, lang)}
    >
      <div className="relative shrink-0">
        <div
          style={{ minHeight: 200 }}
          className="overflow-hidden mx-auto w-full sm:w-[180px] h-[180px] md:w-[200px] md:h-[200px] transition duration-200 ease-in-out transform relative"
        >
          <Image
            src={productImage}
            alt={getNameForLanguage(product, lang) || 'Product Image'}
            style={{ objectFit: 'contain' }}
            sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
            className="object-contain"
          />
        </div>
        <div className="w-full h-full absolute top-0 pt-2.5 md:pt-3.5 px-3 md:px-4 lg:px-[18px] z-10 -mx-0.5 sm:-mx-1">
          {discount && (
            <span className="text-[11px] md:text-xs font-bold text-brand-light uppercase inline-block bg-brand rounded-full px-2.5 pt-1 pb-[3px] mx-0.5 sm:mx-1">
              {t('text-on-sale')}
            </span>
          )}
          <div style={{ right: '0.25rem', top: '0.5rem' }} className="absolute">
            {product && <KosherIcon width={60} height={60} product={product} />}
          </div>
          <div className={`block product-count-button-position`}>
            <RenderPopupOrAddToCart product={product} lang={lang} />
          </div>
        </div>
      </div>

      <div className="flex flex-col px-3 md:px-4 lg:px-[18px] pb-5 lg:pb-6 lg:pt-1.5 h-full">
        <div className="mb-1 lg:mb-1.5 -mx-1">
          <span className="inline-block mx-1 text-sm font-semibold sm:text-15px lg:text-base text-brand-dark">
            {product_type === 'variable' ? `${minPrice} - ${maxPrice}` : price}
          </span>
          {basePrice && (
            <del className="mx-1 text-sm text-brand-dark text-opacity-70">
              {basePrice}
            </del>
          )}
        </div>
        <h2 className="text-brand underline text-13px sm:text-sm lg:text-15px leading-5 sm:leading-6 mb-1.5">
          <Link to={`/${lang}/products/${product.slug}`}>
            {getNameForLanguage(product, lang)}
          </Link>
        </h2>
        <ProductUnitLabelTranslations
          product={product}
          className="mt-auto text-13px sm:text-sm"
        />
      </div>
    </article>
  );
};

export default ProductCardAlpine;
